/**
 * Infinite Scroll Auto — simple forward-only scroll-triggered product loading.
 *
 * - Loads next page when user scrolls within SCROLL_THRESHOLD of the grid bottom.
 * - Prefetches PREFETCH_AHEAD pages in the background.
 * - Velocity-aware: prefetches one extra page when the user is scrolling fast.
 *
 * Back-navigation scroll restoration:
 * - Modern browsers preserve state via bfcache where possible.
 * - When bfcache misses, the clicked product page is restored first, then older
 *   pages are hydrated above it after the restored position is visible.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  Pin history.scrollRestoration to 'manual' as early as possible —  */
  /*  ideally before the browser tries its first scroll-restoration     */
  /*  pass. If we wait for DOMContentLoaded, Chrome has already queued  */
  /*  a restore to the old scrollY, and keeps retrying as the document  */
  /*  grows, fighting our scrollTo. We own scroll restoration entirely  */
  /*  on PLPs, so 'manual' is always correct here — no conditional.     */
  /* ------------------------------------------------------------------ */
  try {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  } catch (e) {}

  /* ------------------------------------------------------------------ */
  /*  Debug — flip to false to silence all logging.                      */
  /*                                                                    */
  /*  When true, prints a tagged trace of every restore phase + a       */
  /*  global watcher that logs any scroll jump > SCROLL_JUMP_THRESHOLD  */
  /*  with a short stack so we can see what code caused the snap.       */
  /* ------------------------------------------------------------------ */
  var DEBUG_RESTORE = false;
  var SCROLL_JUMP_THRESHOLD = 200;

  function dbg(label, extra) {
    if (!DEBUG_RESTORE) return;
    try {
      var payload = {
        t: Math.round(performance.now()),
        scrollY: Math.round(window.scrollY || window.pageYOffset || 0),
        docH: document.documentElement ? document.documentElement.scrollHeight : 0,
        innerH: window.innerHeight,
        sr: ('scrollRestoration' in history) ? history.scrollRestoration : 'n/a',
        page: typeof currentPage !== 'undefined' ? currentPage : '?',
        lowest: typeof lowestLoadedPage !== 'undefined' ? lowestLoadedPage : '?',
        total: typeof totalPages !== 'undefined' ? totalPages : '?'
      };
      if (extra) {
        for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) payload[k] = extra[k];
      }
      console.log('[isr]', label, payload);
    } catch (e) {}
  }

  if (DEBUG_RESTORE) {
    // Watch every scroll event from the moment the script loads. If we see a
    // jump bigger than SCROLL_JUMP_THRESHOLD between consecutive ticks, log
    // the delta + a short stack so the offending code path is visible in the
    // console. This is the workhorse for tracking down the snap-back.
    (function () {
      var lastY = window.scrollY || window.pageYOffset || 0;
      window.addEventListener('scroll', function () {
        var y = window.scrollY || window.pageYOffset || 0;
        var delta = y - lastY;
        if (Math.abs(delta) >= SCROLL_JUMP_THRESHOLD) {
          var stack = '';
          try { stack = new Error().stack.split('\n').slice(2, 7).join(' | '); } catch (e) {}
          console.log('[isr] SCROLL-JUMP', {
            from: Math.round(lastY),
            to:   Math.round(y),
            delta: Math.round(delta),
            docH: document.documentElement.scrollHeight,
            stack: stack
          });
        }
        lastY = y;
      }, { passive: true, capture: true });
    })();
  }

  /* ------------------------------------------------------------------ */
  /*  Config                                                             */
  /* ------------------------------------------------------------------ */
  var GRID_SELECTOR        = '#product-grid';
  var PAGINATION_SELECTOR  = '.collection-pagination';
  var SKELETON_CLASS       = 'infinite-scroll-phantom-card';
  var RESTORE_SPACER_CLASS = 'infinite-scroll-restore-spacer';
  var SCROLL_THRESHOLD     = 2000;
  var PREVIOUS_SCROLL_THRESHOLD = 2200;
  var PREFETCH_AHEAD       = 1;
  var SCROLL_DEBOUNCE      = 60;
  var FAST_SCROLL_DELTA    = 800;
  var LOAD_PREVIOUS_PAGES  = false;
  var PREVIOUS_LOAD_COOLDOWN = 900;
  var HTML_CACHE_MAX_PAGES = 12;
  var HTML_CACHE_TTL_MS    = 15 * 60 * 1000; // 15 min — drop stale stock/price data
  var RESTORE_FETCH_CONCURRENCY = 3;

  // Tracking / attribution params that don't change the actual PLP content.
  // Stripping them from the cache key means the same logical PLP shares one
  // cache entry regardless of which ad campaign or referral the user came from.
  var TRACKING_PARAM_PATTERNS = [
    /^utm_/i,        // Google Analytics campaign params
    /^fbclid$/i,     // Facebook click ID
    /^gclid$/i,      // Google Ads click ID
    /^msclkid$/i,    // Microsoft Ads click ID
    /^yclid$/i,      // Yandex click ID
    /^_ga$/i,        // GA cross-domain
    /^_gl$/i,        // GA cross-domain linker
    /^mc_cid$/i,     // Mailchimp campaign
    /^mc_eid$/i,     // Mailchimp email
    /^twclid$/i,     // Twitter click ID
    /^dclid$/i       // DoubleClick
  ];

  /* ------------------------------------------------------------------ */
  /*  State                                                              */
  /* ------------------------------------------------------------------ */
  var currentPage    = 1;
  var lowestLoadedPage = 1;
  var totalPages     = 1;
  var isFetching     = false;
  var isFetchingPrevious = false;
  var prefetchCache  = new Map();
  var scrollHandler  = null;
  var scrollTimer    = null;
  var lastScrollY    = window.scrollY || 0;
  var isRestoring    = false;
  var lastPreviousLoadAt = 0;
  var restoreCompletedAt = 0;
  var nativeScrollRestorationTimer = null;
  var visibilityPromoter = null;
  // Track forward-load failures so we back off instead of retry-storming the
  // server. Reset to 0 on any successful page load.
  var nextPageFailures = 0;
  var nextPageRetryAt  = 0;
  var restoreTrackingAttached = false;

  /* ------------------------------------------------------------------ */
  /*  DOM helpers                                                        */
  /* ------------------------------------------------------------------ */
  function getGrid() { return document.querySelector(GRID_SELECTOR); }

  function getCurrentListingView() {
    var grid = getGrid();
    var view = grid ? grid.getAttribute('data-listing-view') : 'grid';
    return ['grid', 'list', 'compact'].indexOf(view) > -1 ? view : 'grid';
  }

  function getListingLayoutKey() {
    var width = window.innerWidth || document.documentElement.clientWidth || 1024;
    var breakpoint = width < 481 ? 'phone' : width < 768 ? 'mobile' : width < 990 ? 'tablet' : 'desktop';
    return getCurrentListingView() + ':' + breakpoint;
  }

  function isVirtualGridItem(item) {
    return !item
      || item.classList.contains(SKELETON_CLASS)
      || item.classList.contains(RESTORE_SPACER_CLASS);
  }

  function isTrackingParam(key) {
    for (var i = 0; i < TRACKING_PARAM_PATTERNS.length; i++) {
      if (TRACKING_PARAM_PATTERNS[i].test(key)) return true;
    }
    return false;
  }

  function storageKey(name) {
    var url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');

    // Strip tracking params so cache hits aren't fragmented by ad campaign,
    // GA session, etc.
    var keysToStrip = new Set();
    url.searchParams.forEach(function (_, key) {
      if (isTrackingParam(key)) keysToStrip.add(key);
    });
    keysToStrip.forEach(function (key) {
      url.searchParams.delete(key);
    });

    // Stable param ordering — ?a=1&b=2 and ?b=2&a=1 share one cache entry.
    if (typeof url.searchParams.sort === 'function') {
      url.searchParams.sort();
    }

    return name + '_' + url.pathname + url.search;
  }

  function ssGet(name) {
    try { return sessionStorage.getItem(storageKey(name)); } catch (e) { return null; }
  }

  function ssSet(name, value) {
    try { sessionStorage.setItem(storageKey(name), value); } catch (e) {}
  }

  function ssRemove(name) {
    try { sessionStorage.removeItem(storageKey(name)); } catch (e) {}
  }

  function ssGetJSON(name) {
    try { return JSON.parse(ssGet(name)); } catch (e) { return null; }
  }

  function ssSetJSON(name, value) {
    ssSet(name, JSON.stringify(value));
  }

  function setNativeScrollRestoration(value) {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = value;
    }
  }

  function releaseNativeScrollRestorationAfter(delay) {
    if (nativeScrollRestorationTimer) {
      clearTimeout(nativeScrollRestorationTimer);
    }

    nativeScrollRestorationTimer = setTimeout(function () {
      nativeScrollRestorationTimer = null;
      if (!isRestoring) setNativeScrollRestoration('auto');
    }, delay);
  }

  function setGridHidden(hidden, keepManualScrollRestoration) {
    var container = document.getElementById('ProductGridContainer');
    document.documentElement.classList.toggle('plp-smart-restoring', hidden);

    if (hidden || keepManualScrollRestoration) {
      if (nativeScrollRestorationTimer) {
        clearTimeout(nativeScrollRestorationTimer);
        nativeScrollRestorationTimer = null;
      }
      setNativeScrollRestoration('manual');
    } else {
      setNativeScrollRestoration('auto');
    }

    if (!container) return;
    container.style.visibility = hidden ? 'hidden' : '';
  }

  // Wait for two animation frames — one for layout to settle after a
  // synchronous scroll/DOM change, one more for the browser to commit
  // a paint at the new scroll position. Reveals after this run with
  // the page already in its final visual state.
  function nextFrame() {
    return new Promise(function (resolve) {
      requestAnimationFrame(function () {
        requestAnimationFrame(resolve);
      });
    });
  }

  function getSkeletons(pageNum) {
    var selector = '.' + SKELETON_CLASS;
    if (pageNum) selector += '[data-skeleton-page="' + pageNum + '"]';
    return Array.from(document.querySelectorAll(selector));
  }

  function getProductsPerPage() {
    var grid = getGrid();
    if (!grid) return 16;
    var configured = parseInt(grid.dataset.productsPerPage || '0', 10);
    if (!isNaN(configured) && configured > 0) return configured;

    var count = 0;
    Array.from(grid.children).forEach(function (child) {
      if (!isVirtualGridItem(child)) count += 1;
    });
    return Math.max(8, Math.min(count || 16, 36));
  }

  function createSkeletonCard(index, pageNum) {
    var item = document.createElement('li');
    item.className = SKELETON_CLASS;
    item.setAttribute('data-skeleton-page', String(pageNum));
    item.setAttribute('aria-hidden', 'true');
    item.style.setProperty('--phantom-delay', ((index % 8) * 0.06) + 's');
    item.innerHTML = [
      '<div class="card-wrapper product-card-wrapper h-full">',
        '<div class="card h-full">',
          '<div class="infinite-scroll-phantom-card__media"></div>',
          '<div class="infinite-scroll-phantom-card__content">',
            '<span class="infinite-scroll-phantom-card__line infinite-scroll-phantom-card__line--title"></span>',
            '<span class="infinite-scroll-phantom-card__line infinite-scroll-phantom-card__line--price"></span>',
            '<span class="infinite-scroll-phantom-card__swatches"></span>',
          '</div>',
        '</div>',
      '</div>'
    ].join('');
    return item;
  }

  function showSkeleton(pageNum) {
    var grid = getGrid();
    if (!grid || getSkeletons(pageNum).length > 0) return;

    var count = getProductsPerPage();
    var frag = document.createDocumentFragment();
    for (var i = 0; i < count; i++) {
      frag.appendChild(createSkeletonCard(i, pageNum));
    }
    grid.appendChild(frag);
  }

  function hideSkeleton(pageNum) {
    getSkeletons(pageNum).forEach(function (item) { item.remove(); });
  }

  function pageUrl(pageNum) {
    var url = new URL(window.location.href);
    if (pageNum > 1) url.searchParams.set('page', String(pageNum));
    else url.searchParams.delete('page');
    var grid = getGrid();
    if (grid && grid.dataset.id) url.searchParams.set('section_id', grid.dataset.id);
    return url.toString();
  }

  function extractProducts(doc) {
    var grid = doc.querySelector(GRID_SELECTOR);
    if (!grid) return [];
    // Defensive: server HTML should never contain skeletons or spacers, but
    // the cached-page path also flows through here via getCachedProductsPage,
    // and we want one invariant — extractProducts only ever returns real cards.
    return Array.from(grid.children).filter(function (child) {
      return !isVirtualGridItem(child);
    });
  }

  function getPageHtmlCache() {
    return ssGetJSON('plp_page_html_cache') || {};
  }

  function setPageHtmlCache(cache) {
    ssSetJSON('plp_page_html_cache', cache);
  }

  function getPageHeightCache() {
    return ssGetJSON('plp_page_height_cache') || {};
  }

  function setPageHeightCache(cache) {
    ssSetJSON('plp_page_height_cache', pruneByCreatedAt(cache, HTML_CACHE_MAX_PAGES));
  }

  function pruneByCreatedAt(cache, maxEntries) {
    var keys = Object.keys(cache);
    if (keys.length <= maxEntries) return cache;

    keys.sort(function (a, b) {
      return (cache[a].createdAt || 0) - (cache[b].createdAt || 0);
    });

    while (keys.length > maxEntries) {
      delete cache[keys.shift()];
    }
    return cache;
  }

  function cacheProductsPage(pageNum, products) {
    if (!pageNum || !products || !products.length) return;

    var html = products.map(function (node) {
      return node.outerHTML || '';
    }).join('');

    if (!html) return;

    var cache = getPageHtmlCache();
    cache[String(pageNum)] = {
      html: html,
      createdAt: Date.now()
    };
    setPageHtmlCache(pruneByCreatedAt(cache, HTML_CACHE_MAX_PAGES));
  }

  function getCachedProductsPage(pageNum) {
    var cache = getPageHtmlCache();
    var key = String(pageNum);
    var entry = cache[key];
    if (!entry || !entry.html) return null;

    // TTL — drop entries older than HTML_CACHE_TTL_MS so users coming back
    // after a long break don't see stale stock / prices / availability.
    var createdAt = entry.createdAt || 0;
    if (createdAt && Date.now() - createdAt > HTML_CACHE_TTL_MS) {
      delete cache[key];
      setPageHtmlCache(cache);
      // Drop the matching height entry — its layout might no longer match the
      // (now-refetched) HTML and we'd rather measure live than trust a stale
      // pairing.
      var heights = getPageHeightCache();
      if (heights[key]) {
        delete heights[key];
        setPageHeightCache(heights);
      }
      return null;
    }

    var doc = new DOMParser().parseFromString('<ul id="product-grid">' + entry.html + '</ul>', 'text/html');
    var grid = doc.querySelector(GRID_SELECTOR);
    return grid ? Array.from(grid.children) : null;
  }

  function cacheCurrentGridPage(pageNum) {
    var grid = getGrid();
    if (!grid || !pageNum) return;

    var products = Array.from(grid.children).filter(function (item) {
      return !isVirtualGridItem(item);
    });
    cacheProductsPage(pageNum, products);
  }

  function cacheGridPageForItem(item) {
    var grid = getGrid();
    if (!grid || !item) return;

    var page = getPageForProductItem(item);
    var products = Array.from(grid.children).filter(function (node) {
      return !isVirtualGridItem(node) && getPageForProductItem(node) === page;
    });

    cacheProductsPage(page, products);
  }

  // Single-walk variant for the click capture phase: combines HTML + height
  // caching for the clicked item's page only, so PDP navigation isn't blocked
  // measuring every other page in a deep-scrolled grid.
  function cacheItemPageData(item) {
    var grid = getGrid();
    if (!grid || !item) return;

    var page = getPageForProductItem(item);
    var pageItems = Array.from(grid.children).filter(function (node) {
      return !isVirtualGridItem(node) && getPageForProductItem(node) === page;
    });
    if (!pageItems.length) return;

    cacheProductsPage(page, pageItems);
    cachePageHeight(page, measureItemsHeight(pageItems));
  }

  function measureItemsHeight(items) {
    if (!items || !items.length) return 0;

    var scrollY = window.scrollY || window.pageYOffset || 0;
    var top = Infinity;
    var bottom = -Infinity;

    items.forEach(function (item) {
      if (isVirtualGridItem(item)) return;
      var rect = item.getBoundingClientRect();
      if (!rect || rect.height <= 0) return;
      top = Math.min(top, rect.top + scrollY);
      bottom = Math.max(bottom, rect.bottom + scrollY);
    });

    if (top === Infinity || bottom === -Infinity) return 0;
    return Math.max(0, Math.round(bottom - top));
  }

  function cachePageHeight(pageNum, height) {
    if (!pageNum || !height || height <= 0) return 0;

    var cache = getPageHeightCache();
    cache[String(pageNum)] = {
      height: Math.round(height),
      layoutKey: getListingLayoutKey(),
      createdAt: Date.now()
    };
    setPageHeightCache(cache);
    return Math.round(height);
  }

  function cacheLoadedPageHeight(pageNum) {
    var grid = getGrid();
    if (!grid || !pageNum) return 0;

    var items = Array.from(grid.children).filter(function (item) {
      return !isVirtualGridItem(item) && getPageForProductItem(item) === pageNum;
    });

    return cachePageHeight(pageNum, measureItemsHeight(items));
  }

  function cacheLoadedPageHeights() {
    var grid = getGrid();
    if (!grid) return;

    var pages = {};
    Array.from(grid.children).forEach(function (item) {
      if (isVirtualGridItem(item)) return;
      var page = getPageForProductItem(item);
      if (!pages[page]) pages[page] = [];
      pages[page].push(item);
    });

    Object.keys(pages).forEach(function (page) {
      cachePageHeight(parseInt(page, 10), measureItemsHeight(pages[page]));
    });
  }

  function estimatePreviousPagesHeight(targetPage) {
    if (targetPage <= 1) return 0;

    var cache = getPageHeightCache();
    var layoutKey = getListingLayoutKey();
    var fallbackHeight = cacheLoadedPageHeight(currentPage) || 0;
    var total = 0;

    for (var page = 1; page < targetPage; page++) {
      var entry = cache[String(page)];
      var height = entry && entry.layoutKey === layoutKey && entry.height ? parseInt(entry.height, 10) : 0;
      total += height > 0 ? height : fallbackHeight;
    }

    return Math.max(0, Math.round(total));
  }

  function productPathFromUrl(url) {
    try { return new URL(url, window.location.origin).pathname; } catch (e) { return ''; }
  }

  function findProductInGrid(productPath) {
    var grid = getGrid();
    if (!grid || !productPath) return null;

    // HTMLAnchorElement exposes .pathname natively — avoids ~1000 URL parses
    // on a deep-scrolled grid, which is hot during restore.
    var links = grid.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      if (links[i].pathname === productPath) {
        return links[i].closest('#product-grid > li') || links[i];
      }
    }
    return null;
  }

  function scrollToRestorePosition(restore) {
    var target = findProductInGrid(restore.productPath);
    var desiredTop = parseInt(restore.viewportTop || '0', 10);
    if (isNaN(desiredTop)) desiredTop = 0;

    if (target) {
      var rectTop = target.getBoundingClientRect().top;
      var targetScroll = rectTop + window.scrollY - desiredTop;
      var clamped = Math.max(0, Math.round(targetScroll));
      dbg('scrollToRestorePosition:by-target', {
        productPath: restore.productPath,
        targetRectTop: Math.round(rectTop),
        currentScrollY: Math.round(window.scrollY),
        desiredViewportTop: desiredTop,
        computedScrollY: clamped
      });
      // behavior: 'instant' — CSS sets `html { scroll-behavior: smooth }`,
      // and behavior:'auto' in the options dict means "use the CSS value",
      // NOT "instant". Without this our restore scroll becomes a smooth
      // animation that hydration interrupts midway, landing the user on
      // the wrong row.
      window.scrollTo({ top: clamped, left: 0, behavior: 'instant' });
      lastScrollY = window.scrollY || window.pageYOffset || 0;
      return target;
    }

    if (restore.scrollY) {
      dbg('scrollToRestorePosition:fallback-scrollY', { fallbackScrollY: restore.scrollY });
      window.scrollTo({ top: restore.scrollY, left: 0, behavior: 'instant' });
      lastScrollY = restore.scrollY;
    } else {
      dbg('scrollToRestorePosition:nothing-to-restore');
    }
    return null;
  }

  function getFirstRowCount() {
    var grid = getGrid();
    var view = grid ? grid.getAttribute('data-listing-view') : 'grid';
    var width = window.innerWidth || document.documentElement.clientWidth || 1024;

    if (width < 768) {
      if (view === 'list') return 1;
      if (view === 'compact') return 3;
      return 2;
    }

    if (width < 990) return 2;
    return 4;
  }

  function normalizeAppendedImagePriority(item, index, forceLazy) {
    var isFirstRow = !forceLazy && index < getFirstRowCount();
    item.querySelectorAll('img').forEach(function (img) {
      img.setAttribute('loading', isFirstRow ? 'eager' : 'lazy');
      img.setAttribute('fetchpriority', 'auto');
      if (!img.hasAttribute('decoding')) {
        img.setAttribute('decoding', 'async');
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Visibility-based image priority promotion                         */
  /*                                                                    */
  /*  Single IntersectionObserver with a 2-viewport rootMargin.         */
  /*  When a card enters this expanded zone, promote its images to      */
  /*  loading="eager" + fetchpriority="high" so the browser starts the  */
  /*  download ahead of time. Each card is promoted once, then          */
  /*  unobserved. Works for both forward scroll (cards approaching      */
  /*  from below) and restore (cards already in the viewport at         */
  /*  reveal time).                                                     */
  /* ------------------------------------------------------------------ */
  function ensureVisibilityPromoter() {
    if (visibilityPromoter) return visibilityPromoter;
    if (typeof IntersectionObserver === 'undefined') return null;

    visibilityPromoter = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        promoteCardImages(entry.target);
        visibilityPromoter.unobserve(entry.target);
      });
    }, { rootMargin: '100% 0px 100% 0px', threshold: 0 });

    return visibilityPromoter;
  }

  function promoteCardImages(item) {
    if (!item || item._imgPromoted) return;
    item._imgPromoted = true;
    item.querySelectorAll('img').forEach(function (img) {
      // Only promote if currently lazy — avoid undoing a deliberate eager hint.
      if (img.getAttribute('loading') !== 'eager') {
        img.setAttribute('loading', 'eager');
      }
      if (img.getAttribute('fetchpriority') !== 'high') {
        img.setAttribute('fetchpriority', 'high');
      }
    });
  }

  function observeCardsForPromotion(items) {
    var io = ensureVisibilityPromoter();
    if (!io) return;
    var grid = getGrid();
    if (!grid) return;
    var nodes = items || Array.from(grid.children);
    nodes.forEach(function (item) {
      if (isVirtualGridItem(item)) return;
      if (item._promoteObserved || item._imgPromoted) return;
      item._promoteObserved = true;
      io.observe(item);
    });
  }

  function disconnectVisibilityPromoter() {
    if (visibilityPromoter) {
      visibilityPromoter.disconnect();
      visibilityPromoter = null;
    }
  }

  function getPageForProductItem(item) {
    if (!item) return currentPage;
    var appendedPage = parseInt(item.getAttribute('data-infinite-page') || '0', 10);
    if (!isNaN(appendedPage) && appendedPage > 0) return appendedPage;
    return currentPage || 1;
  }

  function collectPreloadImages(maxItems) {
    var grid = getGrid();
    if (!grid) return [];

    var scrollY = window.scrollY || window.pageYOffset || 0;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    // One viewport above and one below — enough for the row the user is
    // looking at plus some buffer, without preloading the entire grid.
    var loadTop = scrollY - vh;
    var loadBottom = scrollY + vh * 2;

    var matches = [];
    var children = grid.children;
    for (var i = 0; i < children.length; i++) {
      var sibling = children[i];
      if (isVirtualGridItem(sibling)) continue;
      var rect = sibling.getBoundingClientRect();
      var top = rect.top + scrollY;
      var bottom = rect.bottom + scrollY;
      if (bottom < loadTop) continue;
      if (top > loadBottom) break; // grid items are roughly in DOM order

      var img = sibling.querySelector('img');
      if (!img) continue;
      var src = img.currentSrc || img.src;
      var srcset = img.getAttribute('srcset') || '';
      var sizes = img.getAttribute('sizes') || '';
      if (!src) continue;

      matches.push({ src: src, srcset: srcset, sizes: sizes });
      if (matches.length >= (maxItems || 8)) break;
    }
    return matches;
  }

  function savePreloadImagesForReturn() {
    try {
      var imgs = collectPreloadImages(8);
      if (!imgs.length) return;
      sessionStorage.setItem(storageKey('plp_preload_imgs'), JSON.stringify({
        imgs: imgs,
        createdAt: Date.now()
      }));
    } catch (e) {}
  }

  function setupRestoreTracking() {
    if (restoreTrackingAttached) return;
    restoreTrackingAttached = true;

    document.addEventListener('click', function (event) {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      var link = event.target.closest('a[href*="/products/"]');
      if (!link) return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download')) return;

      var item = link.closest('#product-grid > li');
      if (!item) return;

      // Synchronous, single-page walk — keeps click latency bounded on deep
      // grids. A pagehide handler does the full-grid sweep as a safety net.
      cacheItemPageData(item);

      var restorePayload = {
        productPath: productPathFromUrl(link.href),
        page: getPageForProductItem(item),
        viewportTop: Math.round(item.getBoundingClientRect().top),
        scrollY: Math.round(window.scrollY || window.pageYOffset || 0),
        createdAt: Date.now()
      };
      ssSetJSON('plp_smart_restore', restorePayload);

      // Pin scroll restoration to 'manual' before navigation. The browser
      // snapshots this PLP's scrollRestoration into the history entry at
      // navigation time — if it's still 'auto' when the user clicks, Chrome
      // will keep trying to restore the old scrollY on back-navigation,
      // fighting our restore. Manual here = our scrollTo wins.
      setNativeScrollRestoration('manual');

      dbg('click->PDP store-intent', restorePayload);
      savePreloadImagesForReturn();
    }, true);
  }

  /* ------------------------------------------------------------------ */
  /*  Fetch + prefetch                                                   */
  /* ------------------------------------------------------------------ */
  function fetchPage(pageNum) {
    if (prefetchCache.has(pageNum)) return Promise.resolve(prefetchCache.get(pageNum));

    var request = fetch(pageUrl(pageNum))
      .then(function (resp) {
        if (!resp.ok) throw new Error('Failed to fetch page ' + pageNum);
        return resp.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        prefetchCache.set(pageNum, doc);
        return doc;
      })
      .catch(function (err) {
        prefetchCache.delete(pageNum);
        throw err;
      });

    prefetchCache.set(pageNum, request);
    return request;
  }

  async function getProductsForPage(pageNum) {
    var cachedProducts = getCachedProductsPage(pageNum);
    if (cachedProducts && cachedProducts.length) {
      return { page: pageNum, products: cachedProducts, fromCache: true };
    }

    var doc = await fetchPage(pageNum);
    var products = extractProducts(doc);
    if (products.length) cacheProductsPage(pageNum, products);
    updateTotalPagesFromDoc(doc);
    prefetchCache.delete(pageNum);
    return { page: pageNum, products: products, fromCache: false };
  }

  async function getProductsForRestore(targetPage) {
    var pageNums = [];
    var results = new Array(targetPage);
    var nextIndex = 0;

    for (var pageNum = 1; pageNum <= targetPage; pageNum++) {
      pageNums.push(pageNum);
    }

    async function worker() {
      while (nextIndex < pageNums.length) {
        var index = nextIndex++;
        var page = pageNums[index];
        try {
          results[index] = await getProductsForPage(page);
        } catch (err) {
          console.error('[infinite-scroll-auto] restore page load failed:', page, err);
          results[index] = { page: page, products: [] };
        }
      }
    }

    var workers = [];
    var workerCount = Math.min(RESTORE_FETCH_CONCURRENCY, pageNums.length);
    for (var i = 0; i < workerCount; i++) {
      workers.push(worker());
    }

    await Promise.all(workers);
    return results.filter(function (result) {
      return result && result.products && result.products.length;
    });
  }

  // Honour the user's network/data preferences before prefetching.
  // - saveData on  → user opted into reducing background traffic, skip
  // - 2g / slow-2g → prefetch likely won't even finish before the user
  //                  needs the page anyway, and it competes with the
  //                  bytes they actually need now
  function shouldPrefetch() {
    if (typeof navigator === 'undefined' || !navigator.connection) return true;
    if (navigator.connection.saveData) return false;
    var et = navigator.connection.effectiveType;
    if (et === 'slow-2g' || et === '2g') return false;
    return true;
  }

  // Defer to idle so prefetching never competes with critical work
  // (LCP image decoding, scroll handlers, custom-element constructors).
  // 2 s timeout means it still fires under sustained activity.
  function whenIdle(fn, timeoutMs) {
    if (typeof window.requestIdleCallback === 'function') {
      return window.requestIdleCallback(fn, { timeout: timeoutMs || 2000 });
    }
    return setTimeout(fn, 1);
  }

  function prefetchUpcoming(extraAhead) {
    if (!shouldPrefetch()) return;

    whenIdle(function () {
      var ahead = PREFETCH_AHEAD + (extraAhead || 0);
      var chain = Promise.resolve();
      for (var i = 1; i <= ahead; i++) {
        (function (offset) {
          chain = chain.then(function () {
            var p = currentPage + offset;
            if (p > totalPages || prefetchCache.has(p)) return;
            return fetchPage(p).catch(function () {});
          });
        })(i);
      }
    }, 2000);
  }

  /* ------------------------------------------------------------------ */
  /*  Append products                                                    */
  /* ------------------------------------------------------------------ */
  function appendProducts(products, pageNum) {
    var grid = getGrid();
    if (!grid) return;
    var newItems = [];
    var frag = document.createDocumentFragment();
    products.forEach(function (node, index) {
      var clone = node.cloneNode(true);
      clone.setAttribute('data-infinite-page', String(pageNum));
      normalizeAppendedImagePriority(clone, index);
      frag.appendChild(clone);
      newItems.push(clone);
    });

    var skeletons = pageNum ? getSkeletons(pageNum) : [];
    if (skeletons.length > 0) {
      grid.insertBefore(frag, skeletons[0]);
      hideSkeleton(pageNum);
    } else {
      grid.appendChild(frag);
    }

    cachePageHeight(pageNum, measureItemsHeight(newItems));
    observeCardsForPromotion(newItems);
  }

  function buildProductsFragment(products, pageNum, forceLazyImages) {
    var frag = document.createDocumentFragment();
    products.forEach(function (node, index) {
      var clone = node.cloneNode(true);
      clone.setAttribute('data-infinite-page', String(pageNum));
      normalizeAppendedImagePriority(clone, index, forceLazyImages);
      frag.appendChild(clone);
    });
    return frag;
  }

  function createRestoreSpacer(height) {
    var item = document.createElement('li');
    item.className = RESTORE_SPACER_CLASS;
    item.setAttribute('aria-hidden', 'true');
    item.style.cssText = [
      'display:block!important',
      'flex:0 0 100%!important',
      'width:100%!important',
      'max-width:100%!important',
      'height:' + Math.max(0, Math.round(height || 0)) + 'px!important',
      'padding:0!important',
      'margin:0!important',
      'border:0!important',
      'pointer-events:none!important'
    ].join(';');
    return item;
  }

  function replaceGridWithTargetPage(page, spacerHeight) {
    var grid = getGrid();
    if (!grid || !page || !page.products || !page.products.length) return;

    disconnectVisibilityPromoter();
    grid.innerHTML = '';

    if (spacerHeight > 0) {
      grid.appendChild(createRestoreSpacer(spacerHeight));
    }

    // Force lazy on the target page — the user lands somewhere in the middle
    // of it (below the spacer), so the "first row" cards aren't actually in
    // view. The visibility promoter eagerifies what's truly visible.
    grid.appendChild(buildProductsFragment(page.products, page.page, true));
    cacheLoadedPageHeight(page.page);
    observeCardsForPromotion();
  }

  function replaceGridWithPageWindow(pages) {
    var grid = getGrid();
    if (!grid) return;

    // The previous grid (and its observed cards) is being thrown out.
    disconnectVisibilityPromoter();
    grid.innerHTML = '';
    pages.forEach(function (page) {
      grid.appendChild(buildProductsFragment(page.products, page.page, page.forceLazy));
    });

    cacheLoadedPageHeights();
    observeCardsForPromotion();
  }

  function getViewportAnchor() {
    var grid = getGrid();
    if (!grid) return null;

    var points = [
      [Math.round((window.innerWidth || document.documentElement.clientWidth || 1) / 2), Math.round((window.innerHeight || document.documentElement.clientHeight || 1) / 2)],
      [Math.round((window.innerWidth || document.documentElement.clientWidth || 1) / 2), Math.min(160, (window.innerHeight || document.documentElement.clientHeight || 1) - 1)]
    ];

    for (var i = 0; i < points.length; i++) {
      var el = document.elementFromPoint(points[i][0], points[i][1]);
      var item = el && el.closest ? el.closest('#product-grid > li') : null;
      if (item && !isVirtualGridItem(item)) return item;
    }

    var children = Array.from(grid.children);
    for (var j = 0; j < children.length; j++) {
      if (!isVirtualGridItem(children[j])) return children[j];
    }
    return null;
  }

  async function hydratePreviousPagesAfterRestore(targetPage) {
    if (targetPage <= 1) return;

    var grid = getGrid();
    var spacer = grid ? grid.querySelector('.' + RESTORE_SPACER_CLASS) : null;
    if (!grid || !spacer) return;

    var prependedAnything = false;
    var spacerEstimatedHeight = spacer.offsetHeight;
    dbg('hydrate:start', { targetPage: targetPage, spacerEstimatedHeight: spacerEstimatedHeight });

    try {
      var pages = await getProductsForRestore(targetPage - 1);
      dbg('hydrate:fetched-prev-pages', { count: pages.length, pageNumbers: pages.map(function (p) { return p.page; }) });
      if (!pages.length || !spacer.parentNode) return;

      pages.forEach(function (page) {
        page.forceLazy = true;
      });

      var anchor = getViewportAnchor();
      var anchorTop = anchor ? anchor.getBoundingClientRect().top : null;
      dbg('hydrate:anchor-pre', {
        anchorFound: !!anchor,
        anchorTop: anchorTop !== null ? Math.round(anchorTop) : null,
        anchorPage: anchor ? anchor.getAttribute('data-infinite-page') : null
      });
      var frag = document.createDocumentFragment();

      pages.forEach(function (page) {
        frag.appendChild(buildProductsFragment(page.products, page.page, true));
      });

      // Measure right before/after the prepend so we can compare estimate vs
      // actual height of the inserted previous-pages block.
      var docHBefore = document.documentElement.scrollHeight;
      grid.insertBefore(frag, spacer);
      prependedAnything = true;
      var docHAfterInsert = document.documentElement.scrollHeight;
      spacer.remove();
      var docHAfterSpacerRemoved = document.documentElement.scrollHeight;
      var actualPrevPagesHeight = docHAfterInsert - docHBefore;
      // Net document growth = (inserted height) - (spacer height removed).
      // If estimate was perfect this should be ~0.
      var netDocDelta = docHAfterSpacerRemoved - docHBefore;
      dbg('hydrate:inserted', {
        actualPrevPagesHeight: actualPrevPagesHeight,
        spacerEstimatedHeight: spacerEstimatedHeight,
        estimateError: actualPrevPagesHeight - spacerEstimatedHeight,
        netDocDelta: netDocDelta
      });
      lowestLoadedPage = 1;
      cacheLoadedPageHeights();
      observeCardsForPromotion();

      if (anchor && anchorTop !== null) {
        var newAnchorTop = anchor.getBoundingClientRect().top;
        var delta = newAnchorTop - anchorTop;
        dbg('hydrate:anchor-compensation', {
          anchorTopBefore: Math.round(anchorTop),
          anchorTopAfter: Math.round(newAnchorTop),
          delta: Math.round(delta),
          willScrollBy: Math.round(delta)
        });
        if (delta) window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
        dbg('hydrate:after-compensation');
      } else {
        dbg('hydrate:no-anchor-skipping-compensation');
      }
    } catch (err) {
      console.error('[infinite-scroll-auto] previous restore hydration failed:', err);
      dbg('hydrate:error', { message: String(err && err.message || err) });
    } finally {
      // If hydration produced no products (all fetches failed or returned
      // empty), the spacer is still sitting above the target page as an
      // invisible gap. Collapse it and absorb its height into the scroll so
      // the user's viewport stays put and the void disappears.
      if (!prependedAnything && spacer && spacer.parentNode) {
        var spacerHeight = spacer.offsetHeight;
        spacer.remove();
        if (spacerHeight) window.scrollBy({ top: -spacerHeight, left: 0, behavior: 'instant' });
        dbg('hydrate:no-products-collapsed-spacer', { spacerHeight: spacerHeight });
      }
      dbg('hydrate:end', { prependedAnything: prependedAnything });
    }
  }

  function schedulePreviousRestoreHydration(targetPage) {
    if (targetPage <= 1) return;

    whenIdle(function () {
      hydratePreviousPagesAfterRestore(targetPage);
    }, 2500);
  }

  function getVisiblePrependAnchor(grid) {
    var items = Array.from(grid.children);
    var viewportTop = 0;
    var viewportBottom = window.innerHeight || document.documentElement.clientHeight || 0;

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (isVirtualGridItem(item)) continue;

      var rect = item.getBoundingClientRect();
      if (rect.bottom > viewportTop && rect.top < viewportBottom) {
        return item;
      }
    }

    return null;
  }

  function preserveAnchor(anchor, anchorTop) {
    if (!anchor) return;
    var newAnchorTop = anchor.getBoundingClientRect().top;
    window.scrollBy({ top: newAnchorTop - anchorTop, left: 0, behavior: 'instant' });
    lastScrollY = window.scrollY || window.pageYOffset || 0;
  }

  function prependProducts(products, pageNum) {
    var grid = getGrid();
    if (!grid || !products.length) return;

    var anchor = getVisiblePrependAnchor(grid);
    var anchorTop = anchor ? anchor.getBoundingClientRect().top : 0;
    var frag = buildProductsFragment(products, pageNum, true);
    var firstNewItem = frag.firstElementChild;
    grid.insertBefore(frag, grid.firstChild);
    preserveAnchor(anchor, anchorTop);

    // Observe newly prepended cards. Walk forward from the first new item
    // until we reach the original first child (now displaced).
    var newItems = [];
    for (var node = firstNewItem; node && !node._promoteObserved; node = node.nextElementSibling) {
      newItems.push(node);
    }
    observeCardsForPromotion(newItems);
  }

  function canLoadPreviousPage() {
    if (!LOAD_PREVIOUS_PAGES) return false;
    if (isFetchingPrevious || isRestoring) return false;
    if (lowestLoadedPage <= 1) return false;
    if (Date.now() - restoreCompletedAt < PREVIOUS_LOAD_COOLDOWN) return false;
    if (Date.now() - lastPreviousLoadAt < PREVIOUS_LOAD_COOLDOWN) return false;
    return true;
  }

  /* ------------------------------------------------------------------ */
  /*  Near-bottom check                                                  */
  /* ------------------------------------------------------------------ */
  function isNearGridBottom() {
    var grid = getGrid();
    if (!grid) return false;
    return grid.getBoundingClientRect().bottom - window.innerHeight < SCROLL_THRESHOLD;
  }

  function isNearGridTop() {
    var grid = getGrid();
    if (!grid) return false;
    return grid.getBoundingClientRect().top > -PREVIOUS_SCROLL_THRESHOLD;
  }

  /* ------------------------------------------------------------------ */
  /*  Load next page                                                     */
  /* ------------------------------------------------------------------ */
  async function loadNextPage() {
    if (isFetching || isRestoring || currentPage >= totalPages) return;
    if (Date.now() < nextPageRetryAt) return;

    var nextPage = currentPage + 1;
    isFetching = true;
    var loadSucceeded = false;

    var cached = prefetchCache.get(nextPage);
    if (!cached || typeof cached.querySelector !== 'function') {
      showSkeleton(nextPage);
    }

    try {
      var doc = await fetchPage(nextPage);
      var products = extractProducts(doc);

      if (products.length > 0) {
        cacheProductsPage(nextPage, products);
        appendProducts(products, nextPage);
        currentPage = nextPage;

        updateTotalPagesFromDoc(doc);

        document.dispatchEvent(new CustomEvent('product-grid-updated'));
        document.dispatchEvent(new CustomEvent('datalayer-pagination-products', { detail: products }));
      } else {
        totalPages = currentPage;
      }

      prefetchCache.delete(nextPage);
      prefetchUpcoming();
      nextPageFailures = 0;
      nextPageRetryAt = 0;
      loadSucceeded = true;

    } catch (err) {
      console.error('[infinite-scroll-auto]', err);
      // Exponential backoff capped at 30 s. Without this, the finally-block
      // recursion combined with the next scroll event would retry a failed
      // page indefinitely while the user is near the bottom.
      nextPageFailures += 1;
      var backoffMs = Math.min(30000, 1000 * Math.pow(2, nextPageFailures - 1));
      nextPageRetryAt = Date.now() + backoffMs;
      // Drop the in-flight prefetch entry so the next attempt re-fetches.
      prefetchCache.delete(nextPage);
    } finally {
      isFetching = false;
      hideSkeleton(nextPage);
      // Only auto-continue on success. On failure, the backoff timer + the
      // user's next scroll event are the things that re-trigger us.
      if (loadSucceeded && currentPage < totalPages && isNearGridBottom()) loadNextPage();
    }
  }

  async function loadPreviousPage() {
    if (!canLoadPreviousPage()) return;

    var previousPage = lowestLoadedPage - 1;
    isFetchingPrevious = true;
    lastPreviousLoadAt = Date.now();

    try {
      var page = await getProductsForPage(previousPage);
      if (!page.products.length) return;

      prependProducts(page.products, previousPage);
      lowestLoadedPage = previousPage;
      prefetchCache.delete(previousPage);

      document.dispatchEvent(new CustomEvent('product-grid-updated'));
    } catch (err) {
      console.error('[infinite-scroll-auto] previous page load failed:', err);
    } finally {
      isFetchingPrevious = false;
    }
  }

  async function restoreTargetPageFirst(restore, targetPage) {
    dbg('restoreTargetPageFirst:start', { targetPage: targetPage, fromCache: !!getCachedProductsPage(targetPage) });
    var page = await getProductsForPage(targetPage);
    dbg('restoreTargetPageFirst:fetched', { targetPage: targetPage, products: page.products.length, fromCache: page.fromCache });
    if (!page.products.length) return false;

    var spacerHeight = estimatePreviousPagesHeight(targetPage);
    dbg('restoreTargetPageFirst:spacer-estimate', { targetPage: targetPage, spacerHeight: spacerHeight });
    replaceGridWithTargetPage(page, spacerHeight);
    dbg('restoreTargetPageFirst:grid-replaced');

    lowestLoadedPage = targetPage;
    currentPage = targetPage;

    // Bail before scrolling if the product isn't on the rendered target page —
    // the fallback path will do a full-window restore and the scroll itself,
    // so we avoid a double-scroll flash.
    var target = findProductInGrid(restore.productPath);
    if (!target) {
      dbg('restoreTargetPageFirst:target-NOT-found-in-rendered-page', { productPath: restore.productPath });
      return false;
    }
    var beforeScrollY = window.scrollY;
    scrollToRestorePosition(restore);
    dbg('restoreTargetPageFirst:scrolled', { beforeScrollY: Math.round(beforeScrollY), afterScrollY: Math.round(window.scrollY) });

    document.dispatchEvent(new CustomEvent('product-grid-updated'));
    ssRemove('plp_smart_restore');

    // Wait for the browser to commit a paint at the new scroll position
    // before the finally block reveals the grid. Without this, the reveal
    // can fire one frame too early and the user sees a flicker.
    await nextFrame();
    dbg('restoreTargetPageFirst:paint-committed');
    schedulePreviousRestoreHydration(targetPage);
    return true;
  }

  async function smartRestoreIfNeeded() {
    var restore = ssGetJSON('plp_smart_restore');
    dbg('smartRestore:read-intent', restore);
    if (!restore || !restore.productPath) return false;
    if (Date.now() - (restore.createdAt || 0) > 30 * 60 * 1000) {
      ssRemove('plp_smart_restore');
      setGridHidden(false);
      dbg('smartRestore:expired-intent');
      return false;
    }

    var targetPage = parseInt(restore.page || '1', 10);
    if (isNaN(targetPage) || targetPage <= 1) {
      ssRemove('plp_smart_restore');
      setGridHidden(false);
      dbg('smartRestore:target-page-<=-1-skipping');
      return false;
    }
    targetPage = Math.min(targetPage, totalPages || targetPage);

    var target = findProductInGrid(restore.productPath);
    if (target) {
      ssRemove('plp_smart_restore');
      setGridHidden(false);
      dbg('smartRestore:product-already-in-grid-skipping');
      return false;
    }

    isRestoring = true;
    setGridHidden(true);
    dbg('smartRestore:begin', { targetPage: targetPage, productPath: restore.productPath });

    try {
      if (await restoreTargetPageFirst(restore, targetPage)) {
        dbg('smartRestore:targetFirstPath-success');
        return true;
      }
      dbg('smartRestore:targetFirstPath-failed-falling-back-to-full-window');

      var pages = await getProductsForRestore(targetPage);
      pages.forEach(function (page) {
        page.forceLazy = page.page < targetPage;
      });

      if (pages.length) {
        replaceGridWithPageWindow(pages);
        lowestLoadedPage = 1;
        currentPage = targetPage;
      }

      scrollToRestorePosition(restore);

      document.dispatchEvent(new CustomEvent('product-grid-updated'));
      ssRemove('plp_smart_restore');

      // Wait for the browser to commit a paint at the new scroll position
      // before the finally block reveals the grid. Without this, the reveal
      // can fire one frame too early and the user sees a flicker.
      await nextFrame();
      return true;
    } catch (err) {
      console.error('[infinite-scroll-auto] smart restore failed:', err);
      return false;
    } finally {
      setGridHidden(false, true);
      isRestoring = false;
      restoreCompletedAt = Date.now();
      // Intentionally do NOT release scrollRestoration back to 'auto'. We
      // own scroll restoration on PLPs for the lifetime of the document;
      // releasing here races with the idle-deferred hydration (which runs
      // up to ~2.5 s later) and lets the browser do a second auto-restore
      // pass mid-hydration. The next real navigation re-pins manual at
      // script-load time.
      dbg('smartRestore:finally-revealed-grid');
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Scroll listener                                                    */
  /* ------------------------------------------------------------------ */
  function processScrollTick() {
    var y = window.scrollY || window.pageYOffset || 0;
    var delta = y - lastScrollY;
    lastScrollY = y;

    if (delta > FAST_SCROLL_DELTA && currentPage < totalPages) {
      prefetchUpcoming(1);
    }

    if (LOAD_PREVIOUS_PAGES && lowestLoadedPage > 1 && !isFetchingPrevious && isNearGridTop()) {
      loadPreviousPage();
    }

    if (currentPage < totalPages && !isFetching && isNearGridBottom()) {
      loadNextPage();
    }
  }

  function onScroll() {
    if (scrollTimer) return;
    // Debounce, then defer the layout reads (isNearGridBottom etc.) to the
    // next animation frame so they batch with whatever the browser is about
    // to paint instead of forcing a synchronous layout on the scroll tick.
    scrollTimer = setTimeout(function () {
      scrollTimer = null;
      requestAnimationFrame(processScrollTick);
    }, SCROLL_DEBOUNCE);
  }

  function startScrollListener() {
    if (scrollHandler) return;
    scrollHandler = onScroll;
    window.addEventListener('scroll', scrollHandler, { passive: true });

    setTimeout(function () {
      if (currentPage < totalPages && !isFetching && isNearGridBottom()) loadNextPage();
    }, 200);
  }

  function stopScrollListener() {
    if (scrollHandler) { window.removeEventListener('scroll', scrollHandler); scrollHandler = null; }
    if (scrollTimer) { clearTimeout(scrollTimer); scrollTimer = null; }
  }

  /* ------------------------------------------------------------------ */
  /*  Pagination helpers                                                 */
  /* ------------------------------------------------------------------ */
  function hidePagination() {
    var pag = document.querySelector(PAGINATION_SELECTOR);
    if (pag) pag.style.display = 'none';
  }

  function readTotalPagesFromDOM() {
    var c = document.querySelector('.collection-pagination');
    if (!c) return 1;
    var items = c.querySelectorAll('.pagination__item');
    var max = 1;
    items.forEach(function (el) {
      var n = parseInt(el.textContent.trim(), 10);
      if (!isNaN(n) && n > max) max = n;
    });
    var next = c.querySelector('.pagination__item--next');
    if (next) {
      var href = next.getAttribute('href') || '';
      var m = href.match(/page=(\d+)/);
      if (m) { var p = parseInt(m[1], 10); if (p > max) max = p; }
      if (max <= 1) max = 2;
    }
    return max;
  }

  function updateTotalPagesFromDoc(doc) {
    var c = doc.querySelector('.collection-pagination');
    if (!c) return;
    var items = c.querySelectorAll('.pagination__item');
    var max = totalPages;
    items.forEach(function (el) {
      var n = parseInt(el.textContent.trim(), 10);
      if (!isNaN(n) && n > max) max = n;
    });
    if (max > totalPages) totalPages = max;
  }

  function readInitialState() {
    // Server may have rendered page > 1 if URL had ?page=, but we no longer stamp it.
    // Just take whatever the server gave us as page 1 of our forward-scroll session.
    var url = new URL(window.location.href);
    var serverPage = parseInt(url.searchParams.get('page') || '1', 10);
    if (isNaN(serverPage) || serverPage < 1) serverPage = 1;
    currentPage = serverPage;
    lowestLoadedPage = serverPage;
    totalPages = readTotalPagesFromDOM();
  }

  function markInitialProducts() {
    var grid = getGrid();
    if (!grid) return;
    Array.from(grid.children).forEach(function (item) {
      if (!isVirtualGridItem(item) && !item.hasAttribute('data-infinite-page')) {
        item.setAttribute('data-infinite-page', String(currentPage || 1));
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Init                                                               */
  /* ------------------------------------------------------------------ */
  async function init() {
    var grid = getGrid();
    if (!grid) return;

    dbg('init:start');
    readInitialState();
    dbg('init:state-read', { currentPage: currentPage, lowestLoadedPage: lowestLoadedPage, totalPages: totalPages });
    markInitialProducts();
    cacheCurrentGridPage(currentPage);
    cacheLoadedPageHeights();
    hidePagination();
    hideSkeleton();
    setupRestoreTracking();
    observeCardsForPromotion();

    if (totalPages <= 1) {
      setGridHidden(false);
      return;
    }

    await smartRestoreIfNeeded();
    prefetchUpcoming();
    startScrollListener();
  }

  /* ------------------------------------------------------------------ */
  /*  bfcache — re-attach the scroll listener if the page was restored.  */
  /*  Browser preserves DOM and scroll position automatically.           */
  /* ------------------------------------------------------------------ */
  window.addEventListener('pageshow', function (e) {
    dbg('pageshow', { persisted: e.persisted });
    if (e.persisted) {
      // bfcache restore. Most browsers preserve scroll position natively, but
      // iOS Safari WebKit honors scrollRestoration='manual' even on bfcache
      // resume and lands at scrollY=0. Read the intent BEFORE removing it so
      // we can rescue the scroll when that happens.
      var restore = ssGetJSON('plp_smart_restore');
      ssRemove('plp_smart_restore');

      if (nativeScrollRestorationTimer) {
        clearTimeout(nativeScrollRestorationTimer);
        nativeScrollRestorationTimer = null;
      }
      document.documentElement.classList.remove('plp-smart-restoring');
      var container = document.getElementById('ProductGridContainer');
      if (container) container.style.visibility = '';
      setNativeScrollRestoration('auto');

      // iOS Safari rescue: if bfcache resumed near the top of the page but we
      // had a saved intent, the browser refused native scroll restoration. Do
      // it ourselves. bfcache preserves the DOM so the target <li> is still
      // in the grid — scrollToRestorePosition finds it by product handle.
      // Other browsers that restored scroll natively land above the 50px
      // threshold, so this branch is a no-op for them.
      var currentY = window.scrollY || window.pageYOffset || 0;
      if (restore && restore.productPath && currentY < 50) {
        dbg('pageshow:ios-rescue', { savedScrollY: restore.scrollY, currentY: currentY });
        scrollToRestorePosition(restore);
      }

      lastScrollY = window.scrollY || window.pageYOffset || 0;
      stopScrollListener();
      startScrollListener();
    }
  });

  /* ------------------------------------------------------------------ */
  /*  Filter/facet reset — facets.js replaces the entire grid.           */
  /* ------------------------------------------------------------------ */
  document.addEventListener('product-grid-updated', function () {
    if (isFetching || isFetchingPrevious || isRestoring) return;
    var g = getGrid();
    if (!g) return;
    // Only reset if facets.js actually rebuilt the grid (no infinite-scroll
    // skeleton in the grid right now).
    if (g.querySelector('.' + SKELETON_CLASS)) return;
    stopScrollListener();
    disconnectVisibilityPromoter();
    prefetchCache.clear();
    readInitialState();
    markInitialProducts();
    cacheCurrentGridPage(currentPage);
    cacheLoadedPageHeights();
    hidePagination();
    hideSkeleton();
    observeCardsForPromotion();
    if (totalPages > 1) { prefetchUpcoming(); startScrollListener(); }
  });

  document.addEventListener('listing-view-changed', function () {
    ssRemove('plp_page_height_cache');
    // Wait two frames so the new view's CSS has had a chance to lay out
    // before we measure — otherwise we cache the previous view's heights
    // under the new layout key.
    requestAnimationFrame(function () {
      requestAnimationFrame(cacheLoadedPageHeights);
    });
  });

  // Safety net for the per-click cache (cacheItemPageData only captures the
  // clicked page). Runs once when the page is suspended/unloaded, so the
  // sessionStorage snapshot is complete for the next restore.
  window.addEventListener('pagehide', function () {
    cacheLoadedPageHeights();
  });

  window.infiniteScrollReset = function () {
    stopScrollListener();
    prefetchCache.clear();
    ssRemove('plp_page_height_cache');
    ssRemove('plp_page_html_cache');
    currentPage = 1;
    lowestLoadedPage = 1;
    readInitialState();
    markInitialProducts();
    cacheCurrentGridPage(currentPage);
    cacheLoadedPageHeights();
    hidePagination();
    if (totalPages > 1) { prefetchUpcoming(); startScrollListener(); }
  };

  /* ------------------------------------------------------------------ */
  /*  Boot                                                               */
  /* ------------------------------------------------------------------ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
